import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Printer, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

/**
 * BulkLabelPrinter — prints barcode label sheets for multiple parts.
 *
 * Props:
 *   open / onClose — dialog control
 *   selectedIds — Set<string> of explicitly selected item IDs (may be empty)
 *   selectAllMode — boolean, true if "select all matching" is active
 *   frozenQuery — { categoryId, search } when selectAllMode is true
 *   items — currently visible items on the page (used as fallback for "all filtered")
 *   totalFiltered — total number of filtered items across all pages
 */
export function BulkLabelPrinter({
  open,
  onClose,
  selectedIds = new Set(),
  selectAllMode = false,
  frozenQuery = null,
  currentFilters = null,
  items = [],
  totalFiltered = 0,
}) {
  const [copies, setCopies] = useState('1');
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  // Determine scope
  const scopeCount = selectAllMode
    ? totalFiltered
    : selectedIds.size > 0
      ? selectedIds.size
      : totalFiltered;

  const scopeLabel = selectAllMode
    ? `All ${totalFiltered.toLocaleString()} filtered parts`
    : selectedIds.size > 0
      ? `${selectedIds.size.toLocaleString()} selected part${selectedIds.size !== 1 ? 's' : ''}`
      : `All ${totalFiltered.toLocaleString()} filtered parts`;

  const copiesNum = Math.max(1, Math.min(20, parseInt(copies) || 1));
  const totalLabels = scopeCount * copiesNum;
  const needsConfirmation = totalLabels > 100 && !confirmed;

  useEffect(() => {
    if (open) setConfirmed(false);
  }, [open]);

  const handlePrint = async () => {
    if (needsConfirmation) {
      setConfirmed(true);
      return;
    }

    setLoading(true);
    try {
      // Collect item IDs to print
      let itemIds;
      if (selectAllMode) {
        const params = {};
        if (frozenQuery?.categoryId) params.categoryId = frozenQuery.categoryId;
        if (frozenQuery?.search) params.searchFilter = frozenQuery.search;
        params.pageSize = 500;
        params.page = 1;
        const res = await api.get('/items', { params });
        itemIds = (res.data.items || []).map(i => i.id);
      } else if (selectedIds.size > 0) {
        itemIds = [...selectedIds];
      } else {
        const params = { pageSize: 500, page: 1 };
        if (currentFilters?.categoryId) params.categoryId = currentFilters.categoryId;
        if (currentFilters?.search) params.searchFilter = currentFilters.search;
        const res = await api.get('/items', { params });
        itemIds = (res.data.items || []).map(i => i.id);
      }

      if (itemIds.length === 0) {
        toast.error('No items to print');
        return;
      }

      // Fetch label data
      const res = await api.post('/items/bulk-labels', { item_ids: itemIds });
      const labelItems = res.data.items || [];

      if (labelItems.length === 0) {
        toast.error('No printable label data found');
        return;
      }

      // Open print window
      const printWindow = window.open('', '_blank', 'width=900,height=700');
      if (!printWindow) {
        toast.error('Please allow pop-ups to print labels');
        return;
      }

      // Build barcode labels HTML
      let labelsHtml = '';
      let barcodeIndex = 0;

      for (const item of labelItems) {
        const code = item.primary_code_value || item.sku || item.item_id.slice(0, 8);
        const displayName = item.item_name;
        const displaySku = item.sku;
        const displayMfr = item.manufacturer_number;

        for (let c = 0; c < copiesNum; c++) {
          labelsHtml += `
            <div class="label">
              <div class="label-content">
                <div class="name">${escapeHtml(displayName)}</div>
                ${displaySku ? `<div class="sku">SKU: ${escapeHtml(displaySku)}</div>` : ''}
                ${displayMfr && !displaySku ? `<div class="sku">MFR: ${escapeHtml(displayMfr)}</div>` : ''}
                <div class="barcode-container">
                  <svg id="barcode-${barcodeIndex}"></svg>
                </div>
              </div>
            </div>
          `;
          barcodeIndex++;
        }
      }

      // Barcode init script
      const barcodeData = [];
      let idx = 0;
      for (const item of labelItems) {
        const code = item.primary_code_value || item.sku || item.item_id.slice(0, 8);
        for (let c = 0; c < copiesNum; c++) {
          barcodeData.push({ index: idx, code });
          idx++;
        }
      }

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Barcode Labels (${labelItems.length} parts)</title>
          <style>
            * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            html, body { font-family: Arial, Helvetica, sans-serif; font-size: 12px; line-height: 1.3; margin: 0; padding: 0; }
            @page { size: letter; margin: 8mm; }
            .labels-container {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 6px;
              padding: 4px;
            }
            .label {
              border: 1px solid #bbb;
              padding: 8px 6px;
              text-align: center;
              min-height: 110px;
              page-break-inside: avoid;
              break-inside: avoid;
              background: white;
              display: flex;
              align-items: center;
              justify-content: center;
            }
            .label-content { width: 100%; }
            .name {
              font-weight: bold;
              font-size: 12px;
              margin-bottom: 2px;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              line-height: 1.2;
            }
            .sku { font-size: 9px; color: #555; margin-bottom: 4px; line-height: 1.2; }
            .barcode-container {
              width: 100%;
              min-height: 50px;
              display: flex;
              justify-content: center;
              align-items: center;
            }
            .barcode-container svg { max-width: 95%; width: 100%; height: auto; }
            @media print {
              html, body { margin: 0; padding: 0; background: white; }
              .labels-container { padding: 0; }
              .label { border: 1px solid #999; }
            }
            @media screen {
              body { padding: 10px; background: #f0f0f0; }
            }
          </style>
          <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.12.1/dist/JsBarcode.all.min.js"><\/script>
        </head>
        <body>
          <div class="labels-container">
            ${labelsHtml}
          </div>
          <script>
            var barcodes = ${JSON.stringify(barcodeData)};
            barcodes.forEach(function(b) {
              try {
                JsBarcode("#barcode-" + b.index, b.code, {
                  format: "CODE128",
                  width: 1.8,
                  height: 45,
                  displayValue: true,
                  fontSize: 10,
                  margin: 2
                });
              } catch(e) { console.error("Barcode error for " + b.code, e); }
            });
            setTimeout(function() { window.print(); }, 500);
          <\/script>
        </body>
        </html>
      `);
      printWindow.document.close();

      // Clear toast message
      const partsWord = labelItems.length === 1 ? 'part' : 'parts';
      if (copiesNum > 1) {
        toast.success(`Generating ${totalLabels.toLocaleString()} barcode labels for ${labelItems.length.toLocaleString()} ${partsWord}`);
      } else {
        toast.success(`Generating barcode labels for ${labelItems.length.toLocaleString()} ${partsWord}`);
      }
      onClose();
    } catch (err) {
      console.error('Bulk print error:', err);
      toast.error(err.response?.data?.detail || 'Failed to generate labels');
    } finally {
      setLoading(false);
    }
  };

  // Label summary line
  const labelSummary = copiesNum > 1
    ? `${totalLabels.toLocaleString()} barcode labels will be generated (${copiesNum} per part)`
    : `${scopeCount.toLocaleString()} barcode label${scopeCount !== 1 ? 's' : ''} will be generated`;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="bulk-label-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Printer className="w-5 h-5" />
            Print Barcode Labels
          </DialogTitle>
          <DialogDescription>
            Generate a printable barcode label sheet for standard letter paper.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          {/* Print Scope */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Print Scope</Label>
            <div className="text-sm bg-muted/50 rounded-lg p-3 border" data-testid="bulk-print-scope">
              <p className="font-medium" data-testid="bulk-scope-label">{scopeLabel}</p>
              <p className="text-xs text-muted-foreground mt-0.5" data-testid="bulk-label-summary">{labelSummary}</p>
            </div>
          </div>

          {/* Copies per Part */}
          <div className="space-y-2">
            <Label htmlFor="bulk-copies" className="text-sm font-medium">Copies per Part</Label>
            <Input
              id="bulk-copies"
              data-testid="bulk-copies-input"
              type="number"
              min="1"
              max="20"
              value={copies}
              onChange={(e) => setCopies(e.target.value)}
              className="w-24 h-9"
            />
          </div>

          {/* Multi-page hint */}
          {totalLabels > 30 && (
            <p className="text-xs text-muted-foreground" data-testid="multi-page-hint">
              This will generate multiple print pages.
            </p>
          )}

          {/* Confirmation for large batches */}
          {needsConfirmation && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg" data-testid="bulk-confirm-warning">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-amber-800 dark:text-amber-300">
                  Large batch: {totalLabels.toLocaleString()} barcode labels for {scopeCount.toLocaleString()} parts
                </p>
                <p className="text-amber-700 dark:text-amber-400 text-xs mt-0.5">
                  Click "Confirm & Print" to proceed.
                </p>
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose} data-testid="bulk-cancel-btn">Cancel</Button>
            <Button
              onClick={handlePrint}
              disabled={loading || scopeCount === 0}
              data-testid="bulk-print-btn"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</>
              ) : needsConfirmation ? (
                <><AlertTriangle className="w-4 h-4 mr-2" />Confirm & Print</>
              ) : (
                <><Printer className="w-4 h-4 mr-2" />Print {totalLabels.toLocaleString()} Labels</>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function escapeHtml(str) {
  if (!str) return '';
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

export default BulkLabelPrinter;
